import { useState } from "react";

const fmtUSD = (n: number) => "$" + Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 });

const CONCEPTS = [
  { concept: "Contractual focus", lesson: "Traditional buys inputs and assets. Performance-based buys the service delivered." },
  { concept: "Payment logic", lesson: "Traditional pays a fixed price. Performance-based pays on availability and units produced." },
  { concept: "Risk allocation", lesson: "Traditional leaves the performance risk with you. Performance-based shares it with the supplier." },
  { concept: "Incentive alignment", lesson: "Traditional has weak incentives. Outcome-based ties supplier payment to your business results." },
];
const COSTS = [
  { trad: 240000, perf: 9000, model: "Output-based" },
  { trad: 480000, perf: 27000, model: "Output-based" },
  { trad: 680000, perf: 36000, model: "Output-based" },
  { trad: 980000, perf: 54000, model: "Outcome-based" },
];

type Step = { time: string; trad: [string, string]; perf: [string, string] };
const SCENARIOS: Record<string, { label: string; intro: string; steps: Step[] }> = {
  logistics: {
    label: "Logistics",
    intro: "A warehouse needs material handling equipment.",
    steps: [
      { time: "Project Start", trad: ["Buy forklifts", "Large upfront CapEx and procurement delays. You pay regardless of performance."], perf: ["Deploy forklifts as a service", "Pay per functional unit. Deployed immediately."] },
      { time: "Need More Capacity", trad: ["Buy 2 more", "New PO, approval cycle and delivery delays."], perf: ["Add 2 subscriptions", "Pay-on-availability. Same-day fleet expansion."] },
      { time: "Swap to Tuggers", trad: ["Buy tuggers + maintenance", "Stranded forklifts. You carry the risk and the maintenance spike."], perf: ["Swap fleet type", "Supplier shares the risk. No stranded assets."] },
      { time: "Scale Again", trad: ["Buy more + maintenance spike", "Maintenance keeps growing on an ageing fleet."], perf: ["Pay for results", "Paid on savings, safety and productivity. Supplier covers maintenance."] },
    ],
  },
  manufacturing: {
    label: "Manufacturing",
    intro: "A factory needs automation on its production line.",
    steps: [
      { time: "Project Start", trad: ["Buy robotic cells", "Large upfront CapEx and long integration lead times. You pay regardless of output."], perf: ["Robots as a service", "Pay per operating hour. Installed and running fast."] },
      { time: "Demand Increases", trad: ["Buy 2 more cells", "New capex approval, supplier queue and commissioning delays."], perf: ["Add 2 cells to the contract", "Pay-per-part produced. Capacity follows demand."] },
      { time: "Product Changeover", trad: ["Retool + new equipment", "Old tooling stranded. You carry downtime and retrofit costs."], perf: ["Supplier reconfigures the line", "Supplier shares the changeover risk. No stranded assets."] },
      { time: "Scale Again", trad: ["Expand line + maintenance spike", "Maintenance and unplanned downtime grow with ageing equipment."], perf: ["Pay for OEE results", "Paid on uptime, yield and quality. Supplier covers maintenance."] },
    ],
  },
  energy: {
    label: "Energy & Facilities",
    intro: "A site needs heating, cooling and power equipment.",
    steps: [
      { time: "Project Start", trad: ["Buy chillers & boilers", "Large upfront CapEx and tender delays. You pay regardless of efficiency."], perf: ["Energy as a service", "Pay per kWh of heat or cooling delivered."] },
      { time: "Site Expansion", trad: ["Buy extra units", "New budget cycle, installation and delivery delays."], perf: ["Expand the service", "Pay-on-availability. Capacity added without capex."] },
      { time: "Regulation Changes", trad: ["Replace for compliance", "Non-compliant units stranded. You carry the regulatory risk."], perf: ["Supplier upgrades technology", "Supplier shares the risk. No stranded assets."] },
      { time: "Scale Again", trad: ["More units + maintenance spike", "Energy waste and repairs grow on an ageing plant."], perf: ["Pay for savings", "Paid on guaranteed energy savings and comfort. Supplier covers maintenance."] },
    ],
  },
  generic: {
    label: "Any equipment",
    intro: "Any organisation that needs equipment to run its operations.",
    steps: [
      { time: "Project Start", trad: ["Buy the equipment", "Large upfront CapEx and procurement delays. You pay regardless of performance."], perf: ["Equipment as a service", "Pay per functional unit. Deployed immediately."] },
      { time: "Need More Capacity", trad: ["Buy more units", "New PO, approval cycle and delivery delays."], perf: ["Add units to the contract", "Pay-on-availability. Fast expansion."] },
      { time: "Needs Change", trad: ["Buy different equipment", "Old assets stranded. You carry the risk and maintenance."], perf: ["Swap equipment type", "Supplier shares the risk. No stranded assets."] },
      { time: "Scale Again", trad: ["Buy more + maintenance spike", "Maintenance keeps growing on ageing assets."], perf: ["Pay for results", "Paid on business outcomes. Supplier covers maintenance."] },
    ],
  },
};

const ProcurementComparison = () => {
  const [step, setStep] = useState(0);
  const [industry, setIndustry] = useState("logistics");
  const scenario = SCENARIOS[industry];
  const TIMELINE = scenario.steps.map((s, i) => ({
    time: s.time,
    ...CONCEPTS[i],
    trad: { label: s.trad[0], note: s.trad[1], cost: COSTS[i].trad },
    perf: { label: s.perf[0], note: s.perf[1], cost: COSTS[i].perf, model: COSTS[i].model },
  }));
  const visible = TIMELINE.slice(0, step + 1);
  const current = TIMELINE[step];
  const trad = "stroke-muted-foreground";
  const perf = "stroke-primary";

  return (
    <section id="solution" className="py-20 bg-background">
      <div className="container mx-auto max-w-6xl px-4">
        <div className="text-center mb-10">
          <span className="inline-block rounded-full border border-primary/30 bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            Solution
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold">Traditional vs. Outocome-Based: see the difference</h2>
          <p className="mt-3 max-w-2xl mx-auto text-muted-foreground">
            Pick your industry and step through the scenario. Buying equipment the traditional way and contracting for performance go in very different directions at each decision.
          </p>
        </div>

        <div className="mb-4 flex flex-col items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Choose your industry</p>
          <div className="inline-flex flex-wrap justify-center gap-1 rounded-xl border border-border bg-card p-1 shadow-md">
            {Object.entries(SCENARIOS).map(([key, s]) => (
              <button
                key={key}
                onClick={() => setIndustry(key)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  industry === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">{scenario.intro}</p>
        </div>

        <div className="flex flex-wrap justify-center gap-2">
          {TIMELINE.map((e, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
                step >= i ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:border-primary/50"
              }`}
            >
              {i + 1}. {e.time}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-xl border border-primary/20 bg-primary/5 p-4 text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Concept {step + 1}: {current.concept}</p>
          <p className="mt-1 text-sm md:text-base">{current.lesson}</p>
        </div>

        {/* Chart — the centerpiece */}
        <div className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-lg">
          <svg viewBox="0 0 440 200" className="w-full" aria-label="Cost comparison chart">
              {[40, 80, 120, 160].map((y) => (
                <line key={y} x1="40" y1={y} x2="410" y2={y} className="stroke-border" strokeWidth="1" />
              ))}
              <text x="14" y="100" className="fill-muted-foreground" fontSize="10" textAnchor="middle" transform="rotate(-90,14,100)">COST</text>
              <text x="225" y="196" className="fill-muted-foreground" fontSize="10" textAnchor="middle">TIME →</text>
              <g className={trad} strokeWidth="1.5" fill="none">
                <polyline points="50,140 75,140" />
                <line x1="75" y1="140" x2="90" y2="140" strokeDasharray="3,2" />
                <polyline points="90,140 90,110 165,110" />
                {step >= 1 && <><line x1="165" y1="110" x2="178" y2="110" strokeDasharray="3,2" /><polyline points="178,110 178,76 260,76" /></>}
                {step >= 2 && <><line x1="260" y1="76" x2="270" y2="76" strokeDasharray="3,2" /><polyline points="270,76 270,50 355,50" /></>}
                {step >= 3 && <polyline points="355,50 355,28 405,28" />}
              </g>
              <g className={perf} strokeWidth="2" fill="none">
                <line x1="50" y1="155" x2={step >= 1 ? 165 : 115} y2={step >= 1 ? 145 : 150} />
                {step >= 1 && <line x1="165" y1="145" x2={step >= 2 ? 260 : 215} y2={step >= 2 ? 138 : 141} />}
                {step >= 2 && <line x1="260" y1="138" x2={step >= 3 ? 355 : 310} y2={step >= 3 ? 130 : 134} />}
                {step >= 3 && <line x1="355" y1="130" x2="405" y2="126" />}
              </g>
              {/* Hourglasses on the Traditional steps: every delay costs time */}
              <text x="118" y="102" fontSize="12" textAnchor="middle">⏳</text>
              {step >= 1 && <text x="208" y="68" fontSize="12" textAnchor="middle">⏳</text>}
              {step >= 2 && <text x="300" y="42" fontSize="12" textAnchor="middle">⏳</text>}
              {step >= 3 && <text x="380" y="20" fontSize="12" textAnchor="middle">⏳</text>}
            </svg>
          <div className="mt-4 flex items-center justify-center gap-6 text-xs font-semibold">
            <span className="flex items-center gap-2 text-muted-foreground"><span className="h-0.5 w-8 bg-muted-foreground inline-block" /> Traditional (cost-plus)</span>
            <span className="flex items-center gap-2 text-primary"><span className="h-1 w-8 bg-primary inline-block rounded-full" /> Performance-based</span>
          </div>
        </div>

        {/* Step details */}
        <div className="mt-8 space-y-3">
            {visible.map((ev, i) => (
              <div key={i} className={`grid gap-3 sm:grid-cols-2 transition-opacity ${i === step ? "" : "opacity-60"}`}>
                <div className="rounded-xl border border-border bg-card p-4 shadow-md">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Traditional · {ev.time}</p>
                  <p className="mt-2 text-sm font-semibold">{ev.trad.label}</p>
                  <p className="text-xl font-bold">{fmtUSD(ev.trad.cost)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{ev.trad.note}</p>
                </div>
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 shadow-md">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">{ev.perf.model} · {ev.time}</p>
                  <p className="mt-2 text-sm font-semibold">{ev.perf.label}</p>
                  <p className="text-xl font-bold text-primary">
                    {fmtUSD(ev.perf.cost)}<span className="text-sm font-normal text-muted-foreground">/mo</span>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">{ev.perf.note}</p>
                </div>
              </div>
            ))}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="flex-1 rounded-lg border border-border bg-card py-2.5 text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
              >
                ← Previous
              </button>
              <button
                onClick={() => setStep((s) => Math.min(TIMELINE.length - 1, s + 1))}
                disabled={step === TIMELINE.length - 1}
                className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:pointer-events-none disabled:opacity-30"
              >
                Next step →
              </button>
            </div>
          </div>
      </div>
    </section>
  );
};

export default ProcurementComparison;
